import { useState } from 'react'
import { Modal, PlusIcon } from './modal.jsx'
import {
  InvalidCloudAccountError,
  isRole,
  roleHasCapability,
  validateRegistration,
} from '@qim/shared'
import { ApiError, fetchBootstrapTemplate, registerAccount } from '../api.js'
import { AccountLink } from './account-link.jsx'
import { TemplateButton } from './account.jsx'
import type { RegisteredAccountResult } from '../api.js'
import type { Cloud, CloudAccountRegistration, DelegationScope } from '@qim/shared'
import type { FormEvent, JSX } from 'react'

/**
 * Registering a cloud account, and handing over what it mints.
 *
 * ## The cloud comes first (IM-44)
 *
 * Each cloud's account is a different shape — twelve digits on AWS, a
 * subscription and its tenant on Azure — so the form asks which cloud before
 * it shows any field, and then shows only that branch's. Picking for the
 * person would be the prototype's bug in a form: a value read as whatever
 * cloud the default said.
 *
 * On Azure registering mints nothing to copy. Lighthouse has no external id;
 * the delegation template carries a mark minted for this registration, so the
 * result offers the template rather than a value, and says nothing is lost by
 * leaving. Which scope is delegated is the customer's statement, with no
 * default: Reader on a shared subscription reads every Log Analytics workspace
 * in it.
 *
 * ## Why this screen matters more than it looks
 *
 * This is the one write in the product that cannot be repeated. The API mints
 * the external ID here, returns it exactly once, and there is deliberately no
 * read path afterwards — the listing projects columns that exclude the
 * ciphertext, and recovering one is unbuilt on purpose because it should be its
 * own audited action.
 *
 * So this component has exactly one chance to put that value in front of a
 * person, and it has to *say* that rather than looking like something they can
 * come back for. It was previously a `curl`, where the value scrolled past in a
 * terminal — the worst available place for it.
 *
 * ## Why the form calls the API's own validator
 *
 * `validateRegistration` is the function the handler uses. Calling it here means
 * the form refuses exactly what the API refuses, with the same message, before
 * making a request — rather than a second set of rules that can drift. It lives
 * in `shared` precisely so both sides can reach it.
 */
export const RegisterAccount = ({
  baseUrl,
  tenantId,
  role,
  onRegistered,
  onOpen,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly role: string
  readonly onRegistered: () => void
  /** Navigates in place, to the new account's onboarding screen (IM-181). */
  readonly onOpen: (path: string) => void
}): JSX.Element | null => {
  const [cloud, setCloud] = useState<Cloud | undefined>(undefined)
  const [awsAccountId, setAwsAccountId] = useState('')
  const [subscriptionId, setSubscriptionId] = useState('')
  const [azureTenantId, setAzureTenantId] = useState('')
  const [delegationScope, setDelegationScope] = useState<DelegationScope | undefined>(undefined)
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | undefined>(undefined)
  const [result, setResult] = useState<RegisteredAccountResult | undefined>(undefined)
  const [open, setOpen] = useState(false)

  if (!isRole(role) || !roleHasCapability(role, 'account:onboard')) return null

  /** What the form would send, branch by branch — never another cloud's fields. */
  const draft = (): Record<string, unknown> | undefined => {
    switch (cloud) {
      case undefined:
        return undefined
      case 'aws':
        return { cloud, awsAccountId, label }
      case 'azure':
        return { cloud, subscriptionId, azureTenantId, delegationScope, label }
    }
  }

  const touched =
    label !== '' ||
    (cloud === 'aws' && awsAccountId !== '') ||
    (cloud === 'azure' && (subscriptionId !== '' || azureTenantId !== ''))

  /** The API's own rules, run here so a refusal costs no round trip. */
  const validate = (
    body: Record<string, unknown> | undefined,
  ): { registration?: CloudAccountRegistration; problem?: string } => {
    if (body === undefined) return {}
    try {
      return { registration: validateRegistration(body) }
    } catch (error: unknown) {
      return {
        problem:
          error instanceof InvalidCloudAccountError
            ? error.message
            : 'Those details are not valid.',
      }
    }
  }
  const validated = validate(draft())

  /*
   * Shown only once something has been typed: a fresh form full of refusals is
   * noise. An unpicked scope is said by the radios themselves, so while it is
   * unpicked the other fields are checked as though one were — a GUID typed
   * wrong still says so — and the button simply stays disabled.
   */
  const scopeUnpicked = cloud === 'azure' && delegationScope === undefined
  const problem = !touched
    ? undefined
    : scopeUnpicked
      ? validate({ ...draft(), delegationScope: 'subscription' }).problem
      : validated.problem
  const ready = validated.registration !== undefined

  const reset = (): void => {
    setCloud(undefined)
    setAwsAccountId('')
    setSubscriptionId('')
    setAzureTenantId('')
    setDelegationScope(undefined)
    setLabel('')
  }

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    const registration = validated.registration
    if (registration === undefined) return
    setBusy(true)
    setFailure(undefined)
    try {
      const registered = await registerAccount(baseUrl, tenantId, registration)
      setOpen(false)
      setResult(registered)
      reset()
      /*
       * Deliberately not `onRegistered()` here.
       *
       * That reloads the customer screen, and until IM-88 the reload blanked it
       * to `loading` — unmounting this component and destroying `result` before
       * the panel below could render. The external ID is minted once and has no
       * read path, so the reload silently threw away the only copy of the value
       * the customer needs, leaving an account nobody can finish onboarding.
       *
       * `App` no longer unmounts on a re-read, which is the actual fix. This is
       * the second half: the list refreshes when the panel is dismissed, so
       * there is never a reload in flight while the irreplaceable value is on
       * screen. A few seconds of a stale account list is not a comparable cost.
       */
    } catch (error: unknown) {
      setFailure(
        error instanceof ApiError && error.status === 409
          ? // Specifically, not generically: an account already registered is a
            // different situation from a malformed request, and the person is
            // probably looking at the account they already have.
            `That ${cloud === 'azure' ? 'subscription' : 'AWS account'} is already registered for this customer. It is in the list above.`
          : error instanceof ApiError && error.status === 400
            ? 'The control plane refused those details.'
            : 'Could not register the account just now.',
      )
    } finally {
      setBusy(false)
    }
  }

  if (result !== undefined && result.externalId === undefined) {
    /*
     * Azure: nothing was minted to copy, so this is not an alert and leaving
     * loses nothing. The delegation template is the next step, and it is
     * offered here because the Azure onboarding screen is not built yet
     * (IM-45 decides its steps); the account's own screen will offer it too.
     */
    return (
      <section className="panel">
        <h3>Registered {result.account.label}</h3>
        <dl className="detail">
          <dt>Subscription</dt>
          <dd>
            <code>{result.account.subscriptionId}</code>
          </dd>
          <dt>Delegated</dt>
          <dd>
            {result.account.delegationScope === 'subscription'
              ? 'The whole subscription, which is dedicated to Qrvey'
              : 'Each instance’s resource group, one at a time'}
          </dd>
          <dt>Qrvey principal</dt>
          {/* What the delegation's one authorization names: Reader, nothing
            else. */}
          <dd>
            <code>{result.diagnosticsPrincipal}</code>
          </dd>
        </dl>
        <p className="muted">
          Next, the customer deploys the delegation template in their own tenant. It grants Qrvey
          built-in Reader and nothing else, and carries a mark made for this registration, so Qrvey
          acts only on a delegation this template made. There is nothing to copy by hand.
          {result.account.delegationScope === 'subscription'
            ? ' Deploy it once, against this subscription.'
            : ' Deploy it once for the runner’s resource group, and once per instance before that instance’s first run, each time naming the group: it creates the group and delegates only that group.'}
        </p>
        <TemplateButton
          label="Download the delegation template"
          filename={`qrvey-delegation-${result.account.delegationScope ?? 'subscription'}.json`}
          fetch={() => fetchBootstrapTemplate(baseUrl, tenantId, result.account.accountKey)}
        />
        <div className="actions">
          <button
            type="button"
            className="primary"
            onClick={() => {
              setResult(undefined)
              onRegistered()
            }}
          >
            Done
          </button>
        </div>
      </section>
    )
  }

  if (result !== undefined) {
    return (
      <section className="panel">
        <h3>Registered {result.account.label}</h3>
        {/* role="alert" deliberately: this is the only time this value is ever
          shown, so it should be announced rather than merely rendered. */}
        <p role="alert">
          <strong>Copy the external ID now.</strong> It is shown once and cannot be retrieved again
          — not by this console, not by the API, not from the database. If it is lost, the account
          has to be registered again.
        </p>
        <dl className="detail">
          <dt>External ID</dt>
          <dd>
            <code>{result.externalId}</code>
          </dd>
          <dt>Qrvey principal</dt>
          {/* What the customer's trust policy will name. Read-only by design —
            never the deploy role. */}
          <dd>
            <code>{result.diagnosticsPrincipal}</code>
          </dd>
          <dt>Account</dt>
          <dd>{result.account.accountKey}</dd>
        </dl>
        <p className="muted">
          Both values go into the access template, which is on this account&rsquo;s onboarding
          screen with the external ID already filled in — so the usual path is to go there rather
          than to copy this by hand.
        </p>
        <div className="actions">
          {/*
            The usual path, so it is the primary action (IM-181). It said "go
            there" and offered no way to — the new account's onboarding screen
            was reachable only by dismissing this and finding its name in the
            list. Below the external ID, never instead of it: leaving is the
            moment the value is gone, and the warning above is still true.
            Registering is `account:onboard`, which only a role that also holds
            `account:read` has, so AccountLink always renders a link here.
          */}
          <AccountLink
            tenantId={tenantId}
            accountKey={result.account.accountKey}
            role={role}
            onOpen={onOpen}
            className="button-link primary"
          >
            Continue to onboarding <span aria-hidden="true">→</span>
          </AccountLink>
          {/* Dismissal is what refreshes the list — see `submit`. */}
          <button
            type="button"
            className="ghost"
            onClick={() => {
              setResult(undefined)
              onRegistered()
            }}
          >
            Done
          </button>
        </div>
      </section>
    )
  }

  if (!open) {
    return (
      <div className="panel-action">
        <button type="button" className="primary" onClick={() => setOpen(true)}>
          <PlusIcon />
          Register account
        </button>
      </div>
    )
  }

  return (
    /*
     * The *form* is a modal; the result deliberately is not (IM-146).
     *
     * What comes back is the external ID, which is shown exactly once and
     * cannot be retrieved afterwards by anything. A modal can be dismissed by
     * Esc or a click on the backdrop, and losing that value to a stray click
     * means registering the account again — so the result stays a panel with an
     * explicit Done.
     */
    <Modal title="Register a cloud account" onCancel={() => setOpen(false)}>
      <form onSubmit={(event) => void submit(event)}>
        <fieldset className="field kind-choice">
          <legend>Which cloud?</legend>
          <label htmlFor="cloudAws">
            <input
              id="cloudAws"
              type="radio"
              name="cloud"
              checked={cloud === 'aws'}
              onChange={() => setCloud('aws')}
            />{' '}
            AWS
          </label>
          <label htmlFor="cloudAzure">
            <input
              id="cloudAzure"
              type="radio"
              name="cloud"
              checked={cloud === 'azure'}
              onChange={() => setCloud('azure')}
            />{' '}
            Azure
          </label>
        </fieldset>
        {cloud === 'aws' ? (
          <div className="field">
            <label htmlFor="awsAccountId">AWS account id</label>
            <input
              id="awsAccountId"
              value={awsAccountId}
              onChange={(event) => setAwsAccountId(event.target.value)}
              inputMode="numeric"
              aria-describedby="awsAccountId-hint"
            />
            <span id="awsAccountId-hint" className="hint">
              Exactly 12 digits.
            </span>
          </div>
        ) : null}
        {cloud === 'azure' ? (
          <>
            <div className="field">
              <label htmlFor="subscriptionId">Subscription id</label>
              <input
                id="subscriptionId"
                value={subscriptionId}
                onChange={(event) => setSubscriptionId(event.target.value)}
                aria-describedby="subscriptionId-hint"
              />
              <span id="subscriptionId-hint" className="hint">
                A GUID, from the subscription&rsquo;s Overview in the Azure portal.
              </span>
            </div>
            <div className="field">
              <label htmlFor="azureTenantId">Tenant id</label>
              <input
                id="azureTenantId"
                value={azureTenantId}
                onChange={(event) => setAzureTenantId(event.target.value)}
                aria-describedby="azureTenantId-hint"
              />
              <span id="azureTenantId-hint" className="hint">
                The customer&rsquo;s Microsoft Entra tenant that the subscription belongs to. Also a
                GUID, and never the same one.
              </span>
            </div>
            <fieldset className="field kind-choice" aria-describedby="delegationScope-hint">
              <legend>What does the customer delegate?</legend>
              <label htmlFor="scopeSubscription">
                <input
                  id="scopeSubscription"
                  type="radio"
                  name="delegationScope"
                  checked={delegationScope === 'subscription'}
                  onChange={() => setDelegationScope('subscription')}
                />{' '}
                The whole subscription &mdash; it is dedicated to Qrvey
              </label>
              <label htmlFor="scopeResourceGroups">
                <input
                  id="scopeResourceGroups"
                  type="radio"
                  name="delegationScope"
                  checked={delegationScope === 'resource-groups'}
                  onChange={() => setDelegationScope('resource-groups')}
                />{' '}
                Each instance&rsquo;s resource group &mdash; the subscription holds other things
              </label>
              <span id="delegationScope-hint" className="hint">
                Qrvey&rsquo;s read access can query every log workspace in what is delegated, so a
                shared subscription is delegated one resource group at a time.
              </span>
            </fieldset>
          </>
        ) : null}
        {cloud === undefined ? null : (
          <div className="field">
            <label htmlFor="accountLabel">Label</label>
            <input
              id="accountLabel"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              aria-describedby="accountLabel-hint"
            />
            <span id="accountLabel-hint" className="hint">
              What this account is for &mdash; &ldquo;Trial&rdquo;, &ldquo;Production&rdquo;.
            </span>
          </div>
        )}
        {problem === undefined ? null : <p className="failure">{problem}</p>}
        {cloud === 'aws' ? (
          <p role="note" className="muted">
            Registering mints an external ID that is shown <strong>once</strong>, on the next
            screen. Have somewhere to put it before you continue.
          </p>
        ) : null}
        {cloud === 'azure' ? (
          <p role="note" className="muted">
            Nothing is shown once: the delegation template the next screen offers carries everything
            the customer needs.
          </p>
        ) : null}
        <div className="actions">
          <button type="button" className="ghost" onClick={() => setOpen(false)}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={busy || !ready}>
            {busy ? 'Registering…' : 'Register account'}
          </button>
        </div>
        {failure === undefined ? null : <p className="failure">{failure}</p>}
      </form>
    </Modal>
  )
}
